import { useMemo } from 'react';

import { OrganizationStorage } from 'apis/organizationStorage';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useServerStorage } from 'utils/useServerStorage';

export enum CopilotTrialStatus {
  Ineligible = 'ineligible',
  Eligible = 'eligible',
  TrialActive = 'trial-active',
  TrialExpired = 'trial-expired',
  PremiumActive = 'premium-active',
}

const trialEligibleStatuses = [
  CopilotTrialStatus.Eligible,
  CopilotTrialStatus.TrialActive,
  CopilotTrialStatus.TrialExpired,
];

const trialLengthInDays = 30;
const dayInMs = 86400000;

const getTrialStatus = (
  flags: ReturnType<
    typeof useFeatureFlag<'agent-copilot' | 'agent-copilot-trial-eligibility'>
  >,
  copilotTrialStatus: OrganizationStorage['copilot_trial_status'],
) => {
  if (flags.agentCopilot) {
    return CopilotTrialStatus.PremiumActive;
  }

  if (!flags.agentCopilotTrialEligibility) {
    return CopilotTrialStatus.Ineligible;
  }

  if (flags.agentCopilotTrialEligibility) {
    if (isTrialStarted(copilotTrialStatus?.trialStartedAt)) {
      const trialDaysLeft = getTrialDaysLeft(
        copilotTrialStatus?.trialStartedAt,
      );
      if (trialDaysLeft && trialDaysLeft > 0) {
        return CopilotTrialStatus.TrialActive;
      }
      return CopilotTrialStatus.TrialExpired;
    }

    return CopilotTrialStatus.Eligible;
  }

  return CopilotTrialStatus.Ineligible;
};

const isTrialStarted = (trialStartedAt: undefined | string): boolean => {
  return trialStartedAt !== undefined;
};

/**
 * @param trialStartedAt
 * @returns The number of days left in the trial. If the trial has not started
 *   or is in the future, returns null.
 */
const getTrialDaysLeft = (trialStartedAt: undefined | string) => {
  if (trialStartedAt) {
    const trialStartedAtEpoch = Date.parse(trialStartedAt);

    if (isNaN(trialStartedAtEpoch) || Date.now() < trialStartedAtEpoch) {
      // Trial date is not a valid or is in the future
      return null;
    }

    const timeRemaining =
      trialLengthInDays -
      Math.floor((Date.now() - Date.parse(trialStartedAt)) / dayInMs);

    return Math.max(0, Math.min(timeRemaining, trialLengthInDays));
  }

  return null;
};

const hasCopilot = (status: ReturnType<typeof getTrialStatus>) => {
  switch (status) {
    case CopilotTrialStatus.PremiumActive:
    case CopilotTrialStatus.TrialActive:
      return true;
    default:
      return false;
  }
};

const startTrial =
  (set: ReturnType<typeof useServerStorage<'organization'>>['set']) => () => {
    return set({
      copilot_trial_status: (draft) => {
        draft.trialStartedAt = new Date().toISOString();
      },
    });
  };

const stopTrial =
  (set: ReturnType<typeof useServerStorage<'organization'>>['set']) => () => {
    return set({
      // Set the trial to trialInLength days ago
      copilot_trial_status: (draft) => {
        draft.trialStartedAt = new Date(
          Date.now() - trialLengthInDays * dayInMs,
        ).toISOString();
      },
    });
  };

const resetTrial =
  (
    orgSet: ReturnType<typeof useServerStorage<'organization'>>['set'],
    userSet: ReturnType<typeof useServerStorage<'user'>>['set'],
  ) =>
  () => {
    orgSet({
      copilot_trial_status: (draft) => {
        draft.trialStartedAt = undefined;
      },
    });
    userSet({
      copilot_trial: (draft) => {
        draft.visited_trial_page = false;
        draft.dismissed_leftmenu_cta = false;
      },
    });
  };

const setVisitedTrialPage =
  (set: ReturnType<typeof useServerStorage<'user'>>['set']) => () => {
    return set({
      copilot_trial: (draft) => {
        draft.visited_trial_page = true;
      },
    });
  };

const setDismissLeftMenuCTA =
  (set: ReturnType<typeof useServerStorage<'user'>>['set']) => () => {
    return set({
      copilot_trial: (draft) => {
        draft.dismissed_leftmenu_cta = true;
      },
    });
  };

type UseCopilotTrialReturnType = {
  status: ReturnType<typeof getTrialStatus>;
  eligibleForTrial: boolean;
  hasCopilot: ReturnType<typeof hasCopilot>;
  startTrial: ReturnType<typeof startTrial>;
  stopTrial: ReturnType<typeof stopTrial>;
  resetTrial: ReturnType<typeof resetTrial>;
  trialDaysLeft: ReturnType<typeof getTrialDaysLeft>;
  visitedTrialPage: boolean;
  setVisitedTrialPage: ReturnType<typeof setVisitedTrialPage>;
  dismissedLeftMenuCTA: boolean;
  setDismissedLeftMenuCTA: ReturnType<typeof setDismissLeftMenuCTA>;
};

export const useCopilotTrial = (): UseCopilotTrialReturnType => {
  const flags = useFeatureFlag([
    'agent-copilot',
    'agent-copilot-trial-eligibility',
  ]);
  const { data: orgData, set: orgSet } = useServerStorage('organization');
  const { copilot_trial_status: orgCopilotTrialStatus } = orgData;

  const { data: userData, set: userSet } = useServerStorage('user');
  const { copilot_trial: userCopilotTrialStatus } = userData;

  const trialStatus = useMemo(
    () => getTrialStatus(flags, orgCopilotTrialStatus),
    [orgCopilotTrialStatus, flags],
  );

  return useMemo(
    () => ({
      status: trialStatus,
      eligibleForTrial: trialEligibleStatuses.includes(trialStatus),
      hasCopilot: hasCopilot(trialStatus),
      startTrial: startTrial(orgSet),
      stopTrial: stopTrial(orgSet),
      resetTrial: resetTrial(orgSet, userSet),
      trialDaysLeft: getTrialDaysLeft(orgCopilotTrialStatus?.trialStartedAt),
      visitedTrialPage: userCopilotTrialStatus?.visited_trial_page || false,
      setVisitedTrialPage: setVisitedTrialPage(userSet),
      dismissedLeftMenuCTA:
        userCopilotTrialStatus?.dismissed_leftmenu_cta || false,
      setDismissedLeftMenuCTA: setDismissLeftMenuCTA(userSet),
    }),
    [
      trialStatus,
      orgSet,
      orgCopilotTrialStatus?.trialStartedAt,
      userCopilotTrialStatus?.visited_trial_page,
      userCopilotTrialStatus?.dismissed_leftmenu_cta,
      userSet,
    ],
  );
};
